import classNames from "classnames";
import { FC } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useGuardedNavigation } from "hooks/useGuardedNavigation";

import { ReactComponent as LogoSVG } from "assets/images/common/logo.svg";
import { ReactComponent as CompaniesSVG } from "assets/images/menu/companies.svg";
import { ReactComponent as DevicesSVG } from "assets/images/menu/devices.svg";
import { ReactComponent as IncidentsSVG } from "assets/images/menu/incidents.svg";
import { ReactComponent as LogoutSVG } from "assets/images/menu/logout.svg";
import { ReactComponent as PortfoliosSVG } from "assets/images/menu/portfolios.svg";
import { ReactComponent as ReportsSVG } from "assets/images/menu/reports.svg";
import { ReactComponent as SettingsSVG } from "assets/images/menu/settings.svg";
import { ReactComponent as UsersSVG } from "assets/images/menu/users.svg";

import Badge from "components/Badge";
import Button from "components/Button";

import adminAvatar from "assets/images/admin.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { logoutRequest } from "store/slices/authSlice";

import { UserRole } from "entities/common";
import { PAGES } from "utils/links";

type Props = { className?: string };

const Menu: FC<Props> = ({ className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const guardNavigation = useGuardedNavigation();
  const { user } = useAppSelector((state) => state.auth);

  const logoLink = user?.role === UserRole.ADMINISTRATOR ? PAGES.COMPANY_LIST : PAGES.PORTFOLIOS_LIST;

  const menuItems = [
    ...(user?.role === UserRole.ADMINISTRATOR
      ? [
          {
            label: "Companies",
            icon: <CompaniesSVG />,
            link: PAGES.COMPANY_LIST,
            hasNotification: false,
          },
        ]
      : []),
    {
      label: "Portfolios",
      icon: <PortfoliosSVG />,
      link: PAGES.PORTFOLIOS_LIST,
      hasNotification: false,
    },
    {
      label: "Devices",
      icon: <DevicesSVG />,
      link: PAGES.DEVICES_LIST,
      hasNotification: false,
    },
    {
      label: "Incidents",
      icon: <IncidentsSVG />,
      link: PAGES.INCIDENTS_LIST,
      hasNotification: true,
    },
    {
      label: "Reports",
      icon: <ReportsSVG />,
      link: PAGES.REPORTS,
      hasNotification: false,
    },
    {
      label: "Users",
      icon: <UsersSVG />,
      link: PAGES.USERS,
      hasNotification: false,
    },
    {
      label: "Settings",
      icon: <SettingsSVG />,
      link: PAGES.SETTINGS,
      hasNotification: false,
    },
  ];

  const handleLogout = () => {
    guardNavigation(() => dispatch(logoutRequest()));
  };

  const handleNavigate = (event: React.MouseEvent, link: string) => {
    event.preventDefault();
    guardNavigation(() => navigate(link));
  };

  const isMenuItemActive = (link: string) => {
    if (link === PAGES.COMPANY_LIST) {
      return location.pathname === PAGES.COMPANY_LIST || location.pathname.startsWith(`${PAGES.COMPANY_LIST}/`);
    }
    if (link === PAGES.PORTFOLIOS_LIST) {
      return location.pathname === PAGES.PORTFOLIOS_LIST || location.pathname.startsWith(`${PAGES.PORTFOLIOS_LIST}/`);
    }
    if (link === PAGES.SETTINGS) {
      return location.pathname === PAGES.SETTINGS || location.pathname.startsWith(`${PAGES.SETTINGS}/`);
    }
    return location.pathname === link;
  };

  return (
    <aside className={classNames("menu", className)}>
      <Link to={logoLink} className="menu__logo" onClick={(e) => handleNavigate(e, logoLink)}>
        <LogoSVG />
      </Link>

      <ul className="menu__items">
        {menuItems.map((item) => (
          <Link
            key={item.label}
            to={item.link}
            onClick={(e) => handleNavigate(e, item.link)}
            className={classNames("menu__item", { "menu__item--active": isMenuItemActive(item.link) })}
          >
            <li>
              <p>{item.label}</p>

              <Badge isActive={item.hasNotification}>{item.icon}</Badge>
            </li>
          </Link>
        ))}
      </ul>

      <div className="menu__user">
        <img src={adminAvatar} alt="user" />

        <div className="menu__user-info">
          <p className="menu__user-info-name">{user?.role === UserRole.ADMINISTRATOR ? "Administrator" : "User"}</p>
          <span className="menu__user-info-email">{user?.email || ""}</span>
        </div>

        <Button variant="link" className="menu__user-logout" onClick={handleLogout}>
          <LogoutSVG />
        </Button>
      </div>
    </aside>
  );
};

export default Menu;
